import { useState } from "react";
import { Github, Mail } from "lucide-react";

import { requestMagicLink, ssoStartURL } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { useZodForm, z, emailSchema } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/Page";

const schema = z.object({ email: emailSchema });
type Values = z.infer<typeof schema>;

export function Login() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const form = useZodForm(schema, { defaultValues: { email: "" } });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  async function onSubmit(values: Values) {
    setErr("");
    try {
      await requestMagicLink(values.email);
      setSentTo(values.email);
    } catch (e) {
      setErr(humanizeError(e, "We couldn't send the link. Please try again."));
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">CodeBridge</CardTitle>
          <CardDescription>Sign in or create your account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {sentTo ? (
            <div className="rounded-lg bg-muted p-4 text-center text-sm text-muted-foreground">
              Check <span className="font-medium text-foreground">{sentTo}</span> for a sign-in link.
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
              <Field label="Email" htmlFor="email" error={errors.email?.message}>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  aria-invalid={!!errors.email}
                  {...register("email")}
                />
              </Field>
              {err && <p className="text-sm text-destructive">{err}</p>}
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? <Spinner /> : <Mail />} {isSubmitting ? "Sending…" : "Email me a link"}
              </Button>
            </form>
          )}

          <div className="relative">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              or
            </span>
          </div>

          <div className="grid gap-2">
            <Button variant="outline" asChild className="w-full">
              <a href={ssoStartURL("google")}>Continue with Google</a>
            </Button>
            <Button variant="outline" asChild className="w-full">
              <a href={ssoStartURL("github")}>
                <Github /> Continue with GitHub
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
